import React from "react";
import { fmt } from "../lib/framing";

const PAINTING_IMG =
  "https://images.pexels.com/photos/13909116/pexels-photo-13909116.jpeg?auto=compress&cs=tinysrgb&w=900";

const shade = (hex, f) => {
  const h = (hex || "#FBFBF8").replace("#", "");
  const full = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  const n = parseInt(full, 16);
  const r = Math.round(((n >> 16) & 255) * f);
  const g = Math.round(((n >> 8) & 255) * f);
  const b = Math.round((n & 255) * f);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

export const FramePreview = ({ r, unit, mouldingColor = "#C9A66B", matColors = ["#FBFBF8", "#3A3A3A", "#8E8698"], artworkUrl = "" }) => {
  if (!r) return null;

  const ow = r.outer_width;
  const oh = r.outer_height;
  const valid = ow > 0 && oh > 0 && r.painting_width > 0 && r.painting_height > 0;

  if (!valid) {
    return (
      <div data-testid="preview-invalid" className="flex h-full w-full items-center justify-center rounded-lg drafting-grid p-8 text-center">
        <p className="max-w-xs text-sm text-muted-foreground">
          Adjust the inputs — the current values don't produce a valid frame.
        </p>
      </div>
    );
  }

  const face = r.moulding_face_width;
  const rebate = r.rebate_width;
  const pad = Math.max(ow, oh) * 0.16 + 8;
  const VW = ow + pad * 2;
  const VH = oh + pad * 2;
  const fs = Math.max(VW, VH) * 0.026;

  const ox = pad, oy = pad;
  const vx = ox + face, vy = oy + face;
  const line = "#0F172A";
  const mats = r.mat_cuts || [];
  const N = mats.length;
  const matShades = matColors;

  const matRect = (k) => ({
    x: vx + (mats[k].cut_left - rebate),
    y: vy + (mats[k].cut_top - rebate),
    w: mats[k].window_width,
    h: mats[k].window_height,
  });

  // artwork rectangle = last mat window, or full visible opening when no mat
  const art = N > 0 ? matRect(N - 1) : { x: vx, y: vy, w: r.visible_opening_width, h: r.visible_opening_height };

  return (
    <div data-testid="preview-canvas" className="h-full w-full rounded-lg drafting-grid p-3 sm:p-5">
      <svg viewBox={`0 0 ${VW} ${VH}`} className="h-full w-full" preserveAspectRatio="xMidYMid meet">
        {/* moulding */}
        <rect x={ox} y={oy} width={ow} height={oh} fill={mouldingColor} stroke={line} strokeWidth={fs * 0.08} />
        <rect x={vx} y={vy} width={r.visible_opening_width} height={r.visible_opening_height} fill="none" stroke={shade(mouldingColor, 0.65)} strokeWidth={fs * 0.14} />

        {/* visible opening = top mat surface (or artwork if no mat) */}
        {N > 0 ? (
          <rect x={vx} y={vy} width={r.visible_opening_width} height={r.visible_opening_height} fill={matShades[0]} stroke={shade(matShades[0], 0.85)} strokeWidth={fs * 0.05} />
        ) : null}

        {/* each successive mat window reveals the next layer down */}
        {mats.map((mrect, k) => {
          const rr = matRect(k);
          if (k < N - 1) {
            const fill = matShades[k + 1] || matShades[matShades.length - 1];
            return <rect key={k} x={rr.x} y={rr.y} width={rr.w} height={rr.h} fill={fill} stroke={shade(fill, 0.7)} strokeWidth={fs * 0.035} />;
          }
          return null;
        })}

        {/* artwork */}
        <rect x={art.x - fs * 0.15} y={art.y - fs * 0.15} width={art.w + fs * 0.3} height={art.h + fs * 0.3} fill="#E7E2D6" />
        <image href={artworkUrl || PAINTING_IMG} x={art.x} y={art.y} width={art.w} height={art.h} preserveAspectRatio="xMidYMid slice" />
        <rect x={art.x} y={art.y} width={art.w} height={art.h} fill="none" stroke={line} strokeWidth={fs * 0.04} />

        {/* outer width dimension */}
        <g stroke="#0055FF" strokeWidth={fs * 0.05} fill="#0055FF">
          <line x1={ox} y1={oy - pad * 0.45} x2={ox + ow} y2={oy - pad * 0.45} />
          <line x1={ox} y1={oy - pad * 0.45 - fs * 0.6} x2={ox} y2={oy - pad * 0.45 + fs * 0.6} />
          <line x1={ox + ow} y1={oy - pad * 0.45 - fs * 0.6} x2={ox + ow} y2={oy - pad * 0.45 + fs * 0.6} />
          <text x={ox + ow / 2} y={oy - pad * 0.45 - fs * 0.5} fontSize={fs} textAnchor="middle" stroke="none" fontFamily="JetBrains Mono">
            {fmt(ow, unit)} {unit}
          </text>
        </g>
        {/* outer height dimension */}
        <g stroke="#0055FF" strokeWidth={fs * 0.05} fill="#0055FF">
          <line x1={ox - pad * 0.45} y1={oy} x2={ox - pad * 0.45} y2={oy + oh} />
          <line x1={ox - pad * 0.45 - fs * 0.6} y1={oy} x2={ox - pad * 0.45 + fs * 0.6} y2={oy} />
          <line x1={ox - pad * 0.45 - fs * 0.6} y1={oy + oh} x2={ox - pad * 0.45 + fs * 0.6} y2={oy + oh} />
          <text x={ox - pad * 0.45 - fs * 0.4} y={oy + oh / 2} fontSize={fs} textAnchor="middle" stroke="none" fontFamily="JetBrains Mono" transform={`rotate(-90 ${ox - pad * 0.45 - fs * 0.4} ${oy + oh / 2})`}>
            {fmt(oh, unit)} {unit}
          </text>
        </g>
      </svg>
    </div>
  );
};
